import {
  DvFilterItem,
  DvGridComponent,
  DvQuickFilterComponent,
  DvToolbarAction,
} from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  DvDynamicFormModule,
  DvFormUtils,
  DvPanelModule,
  FormOf,
} from '@devinco/ui/legacy';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgGridModule } from 'ag-grid-angular';
import { OnCallGridService } from '@pages/on-call-page/components/on-call-grid/services/on-call-grid.service';
import { Entity } from '@shared/constants/entity';
import { DirectivesModule } from '@shared/directives/directives.module';
import {
  FilterChangedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowDataUpdatedEvent,
} from 'ag-grid-community';
import {
  OnCallDto,
  OnCallFilterDto,
  OnCallGridPanelForm,
} from '@pages/on-call-page/interfaces';
import { OnCallFilteredItemsService } from '@pages/on-call-page/services/on-call-filtered-items.service';
import { FormGroup } from '@angular/forms';
import { PipesModule } from '@shared/pipes/pipes.module';
import { FilterCondition, OnCallStatus } from '@pages/on-call-page/constants';
import { OnCallGridPanelElementsService } from './services/on-call-grid-panel-elements.service';
import DynamicFormUtils from '@shared/utils/dynamic-form.utils';
import { debounceTime, filter, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OnCallGridPanelService } from './services/on-call-grid-panel.service';
import {
  FilterModel,
  GridContext,
} from '@features/vt-grid/interfaces/grid-context';
import { DvDynamicFormElement } from '@devinco/ui/legacy';
@Component({
  imports: [
    AgGridModule,
    DvGridComponent,
    DvQuickFilterComponent,
    TranslateModule,
    DirectivesModule,
    DvPanelModule,
    PipesModule,
    DvDynamicFormModule,
  ],
  selector: 'app-on-call-grid',
  templateUrl: './on-call-grid.component.html',
  styleUrls: ['./on-call-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [GridOptionsService],
})
export class OnCallGridComponent implements OnInit, OnDestroy {
  gridFiltersCleared = output<void>();
  gridContext: GridContext<OnCallDto>;
  protected gridOptions: GridOptions;
  protected quickFilters: DvFilterItem[];
  protected actions: DvToolbarAction[];
  protected isGridReady = false;
  protected alwaysShowFilterButton = false;

  protected panelFormGroup: FormGroup<FormOf<OnCallGridPanelForm>>;
  protected locationStatusElements: DvDynamicFormElement[];
  protected departmentUserLocationElements: DvDynamicFormElement[];
  protected userPropertyElements: DvDynamicFormElement[];
  protected vehiclePropertyElements: DvDynamicFormElement[];

  private filteredNodes: IRowNode<OnCallDto>[] = [];
  private destroyRef = inject(DestroyRef);

  private gridOptionsService = inject(GridOptionsService);
  private translate = inject(TranslateService);
  private gridService = inject(OnCallGridService);
  private onCallFilteredItemsService = inject(OnCallFilteredItemsService);
  private gridPanelElementsService = inject(OnCallGridPanelElementsService);
  private gridPanelFilterModelService = inject(OnCallGridPanelService);

  ngOnInit(): void {
    this.setupGrid();
    this.setupGridPanelForm();
    this.listenToGridPanelFormChanges();
  }

  ngOnDestroy(): void {
    this.onCallFilteredItemsService.onCallGridFilterChanged$.next({
      items: [],
    });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridService.gridApi = event.api;
    this.gridContext = event.context;
  }

  onRowDataUpdated(event: RowDataUpdatedEvent): void {
    this.saveFilteredNodes(event.api);
    this.setSelectedNodes(event.api);
    this.onCallFilteredItemsService.onCallGridFilterChanged$.next({
      items: this.filteredNodes.map(node => node.data!),
      source: 'rowDataUpdated',
    });
  }

  onFilterChanged(event: FilterChangedEvent): void {
    this.saveFilteredNodes(event.api);
    this.onCallFilteredItemsService.onCallGridFilterChanged$.next({
      items: this.filteredNodes.map(node => node.data!),
      source: 'columnFilter',
    });
    this.setSelectedNodes(event.api);
    this.alwaysShowFilterButton =
      event.api.getDisplayedRowCount() !==
      this.onCallFilteredItemsService.onCallGridFilterChanged$.getValue().items
        .length;
  }

  filtersCleared(): void {
    this.panelFormGroup.reset({
      VehicleCondition: FilterCondition.And,
      UserCondition: FilterCondition.And,
    });
    this.gridService.gridApi.deselectAll();
    this.gridFiltersCleared.emit();
  }

  getPanelFormRawValue(): OnCallGridPanelForm {
    return this.panelFormGroup.getRawValue();
  }

  mapPanelFormDataForUpsert(): Partial<OnCallFilterDto> {
    const formData = this.getPanelFormRawValue();
    return {
      FilterModel: this.gridService.gridApi.getFilterModel(),
      Department: formData.Department?.map(id => ({
        DepartmentID: id,
      })),
      User: (formData.User?.map(id => ({ UserID: id })) || []) as unknown as {
        UserID: number;
      }[],
      Location: formData.Location?.map(l => ({ Description: l })),
      VehicleTag: {
        values: formData.VehicleTag || [],
        operator: formData.VehicleCondition,
      } as FilterModel,
      UserTag: {
        values: formData.UserTag || [],
        operator: formData.UserCondition,
      } as FilterModel,
      OnCallStatus: formData.ShowOnlyStandby ? OnCallStatus.OnCall : undefined,
    };
  }

  getSelectedRowData(): OnCallDto[] {
    return this.onCallFilteredItemsService.onCallGridFilterChanged$.getValue()
      .items;
  }

  setGridPanelFormValue(data: OnCallFilterDto): void {
    const { controls } = this.panelFormGroup;

    controls.Department?.setValue(data.Department?.map(d => d.DepartmentID!));
    DynamicFormUtils.setDropdownElementItems(
      this.departmentUserLocationElements,
      'Department',
      data.Department || []
    );

    DynamicFormUtils.setDropdownElementItems(
      this.departmentUserLocationElements,
      'User',
      data.User || []
    );
    controls.User?.setValue(data.User?.map(d => d.UserID!));

    DynamicFormUtils.setDropdownElementItems(
      this.departmentUserLocationElements,
      'Location',
      data.Location || []
    );
    controls.Location?.setValue(data.Location?.map(d => d.Description!));

    controls.VehicleCondition?.setValue(
      (data.VehicleTag?.operator as FilterCondition | undefined) ||
        FilterCondition.Or
    );

    controls.UserCondition?.setValue(
      (data.UserTag?.operator as FilterCondition | undefined) ||
        FilterCondition.Or
    );
    controls.VehicleTag?.setValue((data.VehicleTag?.values || []) as string[]);
    controls.UserTag?.setValue((data.UserTag?.values || []) as string[]);
    controls.ShowOnlyStandby?.setValue(
      data.OnCallStatus === OnCallStatus.OnCall
    );
  }

  activeFilterChangedCallback(selectedFilterData: OnCallFilterDto): void {
    this.panelFormGroup.markAsPristine();
    this.panelFormGroup.reset();
    this.gridService.gridApi.deselectAll();
    if (selectedFilterData.AgGridFilter) {
      this.gridService.gridApi.setFilterModel(selectedFilterData.AgGridFilter);
    }
    this.setGridPanelFormValue(selectedFilterData);
  }

  private listenToGridPanelFormChanges(): void {
    this.panelFormGroup.valueChanges
      .pipe(
        debounceTime(200),
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.panelFormGroup.dirty),
        switchMap(_ => {
          const filterModel = this.gridService.gridApi.getFilterModel();
          const formValue = this.getPanelFormRawValue();
          if (filterModel['OnCallStatus'] && !formValue.ShowOnlyStandby) {
            delete filterModel['OnCallStatus'];
          }

          const userTagControl = this.panelFormGroup.controls.UserTag;
          if (userTagControl?.dirty && !userTagControl.value?.length) {
            delete filterModel['UserTag'];
          }

          const vehicleTagControl = this.panelFormGroup.controls.VehicleTag;
          if (vehicleTagControl?.dirty && !vehicleTagControl.value?.length) {
            delete filterModel['VehicleTag'];
          }

          const payload = {
            ...this.gridPanelFilterModelService.buildGetOnCallFilterModel(
              formValue
            ),
            FilterModel: filterModel,
          };
          return this.gridPanelFilterModelService.getFilterModel(payload);
        })
      )
      .subscribe(val => {
        this.gridService.gridApi.setFilterModel(val);
      });
  }

  private setupGrid(): void {
    this.quickFilters = this.getQuickFilters();
    this.actions = this.gridService.getGridActions();
    this.gridOptions = {
      ...this.gridOptionsService.clientSide(
        this.gridService.getColumnDefs(),
        Entity.OnCall
      ),
      rowSelection: {
        mode: 'multiRow',
        enableClickSelection: false,
        checkboxes: false,
        headerCheckbox: false,
      },
      suppressDragLeaveHidesColumns: true,
    };
  }

  private getQuickFilters(): DvFilterItem[] {
    return [
      {
        defaultActive: true,
        title: this.translate.instant('OnCall.All'),
        gridFilters: {},
        suppressDeselect: true,
      },
      {
        title: this.translate.instant('OnCall.Location'),
        gridFilters: {
          IsLocation: { filterType: 'set', values: ['1'] },
        },
        suppressDeselect: true,
      },
      {
        title: this.translate.instant('OnCall.Vehicle'),
        gridFilters: {
          IsVehicle: { filterType: 'set', values: ['1'] },
        },
        suppressDeselect: true,
      },
    ];
  }

  private saveFilteredNodes(api: GridApi<OnCallDto>): void {
    this.filteredNodes = this.getFilteredNodes(api);
  }

  private getFilteredNodes(api: GridApi<OnCallDto>): IRowNode<OnCallDto>[] {
    const filteredNodes: IRowNode<OnCallDto>[] = [];
    api.forEachNodeAfterFilter(node => {
      filteredNodes.push(node);
    });

    return filteredNodes;
  }

  private setSelectedNodes(api: GridApi<OnCallDto>): void {
    this.filteredNodes.forEach(node =>
      api.getRowNode(node.id!)?.setSelected(true, false, 'rowDataChanged')
    );
  }

  private setupGridPanelForm(): void {
    this.locationStatusElements =
      this.gridPanelElementsService.locationStatusElements();
    this.departmentUserLocationElements =
      this.gridPanelElementsService.getDepartmentUserLocationElements();
    this.userPropertyElements =
      this.gridPanelElementsService.getUserPropertyEelements();
    this.vehiclePropertyElements =
      this.gridPanelElementsService.getVehiclePropertyElements();

    this.panelFormGroup = DvFormUtils.createFormGroup([
      ...this.locationStatusElements,
      ...this.departmentUserLocationElements,
      ...this.vehiclePropertyElements,
      ...this.userPropertyElements,
    ]);
  }
}
